import { IconeAlerta, IconeCheque, IconeHistorico } from '../../../components/Icones'
import { cn } from '../../../lib/cn'
import { dataHora } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { OcorrenciaDaCarga, RespostaDaCarga } from '../../../types/chamado'
import { competenciaPorExtenso } from '../competencia'

/**
 * O que a carga fez, em números e em listas.
 *
 * Os quatro contadores aparecem SEMPRE, inclusive zerados, e as três listas aparecem inteiras. É o
 * contrário de "importado com sucesso": uma carga que recusa 12 linhas e diz sucesso some com 12
 * chamados de um fechamento sem que ninguém veja — e o fechamento seguinte sai com um denominador
 * menor, que é exatamente o número que alguém assina.
 *
 * A frase de resumo vem pronta do servidor. Reescrevê-la aqui criaria uma segunda versão do mesmo
 * texto, e a do histórico de cargas continuaria sendo a primeira: duas frases para o mesmo fato
 * divergem no primeiro ajuste.
 *
 * @param props a resposta da carga
 * @returns o resumo do que entrou, do que mudou e do que ficou de fora
 */
export function ResumoDaCarga({ resposta }: { resposta: RespostaDaCarga }) {
  const { carga, mensagem } = resposta
  const houveRecusa = carga.recusados > 0

  return (
    <div className="space-y-4">
      <div
        className={cn(
          'flex gap-3 rounded-xl border px-4 py-3 text-sm',
          carga.jaImportado
            ? 'border-slate-300 bg-slate-100 text-slate-800'
            : houveRecusa
              ? 'border-amber-300 bg-amber-50 text-amber-900'
              : 'border-green-300 bg-green-50 text-green-900',
        )}
      >
        <Sinal jaImportado={carga.jaImportado} houveRecusa={houveRecusa} />
        <div className="min-w-0 space-y-1">
          <p className="max-w-prose font-medium">{mensagem}</p>
          <p className="max-w-prose text-xs">
            Arquivo <span className="font-medium">{carga.arquivo}</span> · mês de referência{' '}
            <span className="font-medium">{competenciaPorExtenso(carga.competencia)}</span> · dado referente a{' '}
            <span className="font-medium">{dataHora(carga.coletadoEm)}</span>
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Contador rotulo={TEXTOS.importacao.linhasLidas} valor={carga.linhasLidas} />
        <Contador rotulo={TEXTOS.importacao.novos} valor={carga.novos} />
        <Contador rotulo={TEXTOS.importacao.alterados} valor={carga.alterados} />
        <Contador rotulo={TEXTOS.importacao.semMudanca} valor={carga.semMudanca} />
        <Contador rotulo={TEXTOS.importacao.recusados} valor={carga.recusados} destacar={houveRecusa} />
      </dl>

      <ListaDeOcorrencias titulo={TEXTOS.importacao.recusas} ocorrencias={carga.recusas} tom="recusa" />
      <ListaDeOcorrencias titulo={TEXTOS.importacao.avisos} ocorrencias={carga.avisos} tom="aviso" />
      <ListaDeOcorrencias titulo={TEXTOS.importacao.ignoradas} ocorrencias={carga.ignoradas} tom="neutro" />
    </div>
  )
}

/** O ícone diz de que tipo de resultado se trata antes de a frase ser lida. */
function Sinal({ jaImportado, houveRecusa }: { jaImportado: boolean; houveRecusa: boolean }) {
  if (jaImportado) {
    return <IconeHistorico className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
  }
  if (houveRecusa) {
    return <IconeAlerta className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
  }
  return <IconeCheque className="mt-0.5 h-5 w-5 shrink-0 text-green-700" />
}

/**
 * Um contador da carga.
 *
 * Zero aparece escrito, e não some: "0 linhas recusadas" é uma afirmação — ninguém ficou de fora —,
 * e a ausência do número não afirma nada.
 */
function Contador({ rotulo, valor, destacar }: { rotulo: string; valor: number; destacar?: boolean }) {
  return (
    <div className={cn('rounded-lg border px-3 py-2', destacar ? 'border-amber-300 bg-amber-50' : 'border-slate-200')}>
      <dt className="text-xs text-slate-600">{rotulo}</dt>
      <dd className={cn('text-lg font-bold tabular-nums', destacar ? 'text-amber-900' : 'text-slate-900')}>{valor}</dd>
    </div>
  )
}

const TOM: Record<string, string> = {
  recusa: 'border-red-200 bg-red-50 text-red-900',
  aviso: 'border-amber-200 bg-amber-50 text-amber-900',
  neutro: 'border-slate-200 bg-slate-50 text-slate-700',
}

/**
 * As linhas que não entraram, as que entraram com ressalva e as que não eram dado.
 *
 * Três listas separadas porque são três coisas diferentes: recusa pede correção do arquivo, aviso é
 * achado de fiscalização, e linha ignorada é rodapé de somas. Juntá-las faria o fiscal procurar
 * defeito onde há só o total da planilha.
 */
function ListaDeOcorrencias({
  titulo,
  ocorrencias,
  tom,
}: {
  titulo: string
  ocorrencias: OcorrenciaDaCarga[]
  tom: keyof typeof TOM
}) {
  if (ocorrencias.length === 0) {
    return null
  }
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-800">
        {titulo} ({ocorrencias.length})
      </h3>
      <ul className={cn('mt-2 space-y-1 rounded-lg border px-3 py-2 text-xs', TOM[tom])}>
        {ocorrencias.map((ocorrencia) => (
          <li key={`${ocorrencia.referencia}-${ocorrencia.motivo}`}>
            <span className="font-medium">{ocorrencia.referencia}:</span> {ocorrencia.motivo}
          </li>
        ))}
      </ul>
    </section>
  )
}
